/**
 * Inactivity progression: Active -> Warning -> Locked -> Signout.
 *
 * The "warning" phase shows a countdown modal a configurable lead time before the screen locks;
 * once locked, the user is signed out after a short grace period. Passive activity (activity())
 * resets the countdown only while still "active"; an explicit acknowledgement (reset(), e.g. the
 * warning modal's "Stay signed in") also recovers from "warning". Both are ignored once locked —
 * the session can then only be recovered by re-authenticating after sign-out.
 */
export type InactivityPhase = "active" | "warning" | "locked" | "signout";

export interface InactivityConfig {
    /** Idle time from the last activity until the screen locks. */
    timeoutMs: number;
    /** Lead time before the lock during which the warning countdown is shown. */
    warningMs: number;
    /** Time the screen stays locked before the user is signed out. */
    graceMs: number;
}

export interface InactivityState {
    phase: InactivityPhase;
    /** Epoch ms of the next automatic transition, or null when idle/terminal. Drives the UI countdown. */
    deadline: number | null;
}

/** Default lead time before the lock during which the warning modal counts down. */
export const DEFAULT_WARNING_MS = 60_000;
/** Default time the screen stays locked before the user is signed out. */
export const DEFAULT_GRACE_MS = 30_000;

/** The idle state emitted/seeded when no countdown is in progress. A factory (not a shared const)
 *  so no two consumers — root data, getState(), the overlay fallback — share one mutable object. */
export const idleInactivityState = (): InactivityState => ({ phase: "active", deadline: null });

/** Whether a phase represents the locked-or-signing-out state that must obscure PHI. */
export function isLockedPhase(phase: InactivityPhase): boolean {
    return phase === "locked" || phase === "signout";
}

/**
 * The warning lead actually used, clamped so a short timeout still has an active period before the
 * warning (a 1-minute timeout with a full 1-minute lead would otherwise warn immediately). Shared so
 * every construction path (SharedWorker, per-tab fallback, tests) and the activity relay agree on it.
 */
export function warningLeadMs(config: InactivityConfig): number {
    return Math.min(config.warningMs, Math.floor(config.timeoutMs / 2));
}

/** Message types exchanged between a tab and the SharedWorker (and reused by the per-tab fallback).
 *  Centralized so the worker and the setup share one spelling — a typo in a bare string literal
 *  would otherwise silently drop a reset or a state update with no compile error. */
export const InactivityMessage = {
    /** Tab -> worker: (re)configure the timer with an InactivityConfig payload. */
    Configure: "configure",
    /** Tab -> worker: passive user activity; honored only while still "active". */
    Activity: "activity",
    /** Tab -> worker: explicit "Stay signed in"; honored while not locked. */
    Reset: "reset",
    /** Tab -> worker: recompute the phase from wall-clock now (e.g. on tab refocus). */
    Sync: "sync",
    /** Tab -> worker: disable the timer. */
    Stop: "stop",
    /** Worker -> tab: a new InactivityState. */
    State: "inactivityState",
} as const;

/**
 * Drives the inactivity phase progression off a single set of timers. Pure and DOM-free so it can
 * run identically inside the SharedWorker (one shared timer across all tabs) or, as a fallback,
 * directly in a tab when SharedWorker is unavailable. Emits only on a genuine state change, so
 * frequent activity resets do not spam the consumer.
 */
export class InactivityStateMachine {
    private config: InactivityConfig | null = null;
    private phase: InactivityPhase = "active";
    private timers: ReturnType<typeof setTimeout>[] = [];
    private lastEmitted: InactivityState | null = null;
    /** True while a countdown is scheduled. Cleared on teardown and when the terminal "signout" fires. */
    private armed = false;
    /** Absolute epoch-ms deadlines for the current countdown; the phase is derived from these and
     *  Date.now() (see evaluate()) rather than from which timer happened to fire. Null when idle. */
    private deadlines: { warnAt: number; lockAt: number; signOutAt: number } | null = null;

    constructor(private readonly onChange: (state: InactivityState) => void) {}

    /**
     * (Re)configure the timer. A 0/negative timeout disables it. The countdown is (re)started only
     * on the first configure, when the timeout values actually change, or when the machine has
     * reached its terminal "signout" phase (e.g. a fresh page load after a prior auto sign-out).
     * A redundant configure from another tab during a live countdown is a no-op, so merely opening
     * or reloading a tab cannot reset (and thus indefinitely extend) the shared idle countdown.
     */
    configure(config: InactivityConfig): void {
        const next = config.timeoutMs > 0 ? config : null;
        const changed = !configEquals(this.config, next);
        this.config = next;
        if (!this.armed || changed || this.phase === "signout") {
            this.arm();
        }
    }

    /**
     * Register an explicit acknowledgement (e.g. the warning modal's "Stay signed in"). Restarts the
     * countdown unless the screen is already locked, so it can recover from the "warning" phase.
     */
    reset(): void {
        if (this.phase === "locked" || this.phase === "signout") return;
        this.arm();
    }

    /**
     * Register passive user activity (mouse/keyboard). Restarts the countdown only while still
     * "active"; once the warning is showing it is ignored, so passive activity cannot silently
     * dismiss a warning the user has not acknowledged. This guard is authoritative — unlike a
     * per-tab phase check it cannot be fooled by a tab whose broadcast copy of the phase lags.
     */
    activity(): void {
        if (this.phase !== "active") return;
        this.arm();
    }

    /** Stop all timers and return to the idle "active" state (feature disabled or teardown). */
    stop(): void {
        this.clearTimers();
        this.armed = false;
        this.deadlines = null;
        this.setPhase("active", null);
    }

    /** The most recently emitted state, or the idle default when nothing has been emitted yet. */
    getState(): InactivityState {
        return this.lastEmitted ?? idleInactivityState();
    }

    private arm(): void {
        this.clearTimers();
        this.setPhase("active", null);

        if (!this.config) {
            this.armed = false;
            this.deadlines = null;
            return;
        }

        this.armed = true;
        const { timeoutMs, graceMs } = this.config;
        const warningMs = warningLeadMs(this.config);
        const now = Date.now();
        const lockAt = now + timeoutMs;
        this.deadlines = {
            warnAt: Math.max(now, lockAt - warningMs),
            lockAt,
            signOutAt: lockAt + graceMs,
        };
        this.scheduleEvaluations();
    }

    /**
     * Schedule a wall-clock re-evaluation at each upcoming deadline. The timers only *trigger*
     * evaluate(); the phase itself is derived from Date.now() against the stored deadlines, so a
     * throttled or late-firing timer still lands on the correct phase (e.g. jumps straight to
     * "locked" if the warning window was missed while the worker was backgrounded) rather than
     * replaying a stale earlier transition.
     */
    private scheduleEvaluations(): void {
        if (!this.deadlines) return;
        const now = Date.now();
        const { warnAt, lockAt, signOutAt } = this.deadlines;
        this.schedule(warnAt - now, () => this.evaluate());
        this.schedule(lockAt - now, () => this.evaluate());
        this.schedule(signOutAt - now, () => this.evaluate());
    }

    /**
     * Recompute the phase from the current wall-clock time and the armed deadlines instead of
     * trusting a timer to have fired on schedule. Safe to call at any time — on a scheduled timer or
     * on tab refocus (the "sync" message) — and idempotent via setPhase's dedup. This is what makes
     * a throttled or suspended SharedWorker self-correct: when CPU is next available the true phase
     * is derived from elapsed time, so a tab left idle in the background locks the moment it is
     * looked at again rather than waiting for a long-overdue timer.
     */
    evaluate(): void {
        if (!this.armed || !this.deadlines) return;
        const now = Date.now();
        const { warnAt, lockAt, signOutAt } = this.deadlines;
        if (now >= signOutAt) {
            // Terminal phase: disarm so a later configure (e.g. a fresh page load after the
            // redirect-based sign-out re-initializes the app) is allowed to re-arm the countdown.
            this.armed = false;
            this.setPhase("signout", null);
        } else if (now >= lockAt) {
            this.setPhase("locked", signOutAt);
        } else if (now >= warnAt) {
            this.setPhase("warning", lockAt);
        } else {
            this.setPhase("active", null);
        }
    }

    private schedule(delayMs: number, fn: () => void): void {
        this.timers.push(setTimeout(fn, Math.max(0, delayMs)));
    }

    private setPhase(phase: InactivityPhase, deadline: number | null): void {
        this.phase = phase;
        if (this.lastEmitted && this.lastEmitted.phase === phase && this.lastEmitted.deadline === deadline) {
            return;
        }
        this.lastEmitted = { phase, deadline };
        this.onChange(this.lastEmitted);
    }

    private clearTimers(): void {
        this.timers.forEach(clearTimeout);
        this.timers = [];
    }
}

/** Value-equality for two (possibly null) configs; used to decide whether a configure must re-arm. */
function configEquals(a: InactivityConfig | null, b: InactivityConfig | null): boolean {
    if (!a || !b) return a === b;
    return a.timeoutMs === b.timeoutMs && a.warningMs === b.warningMs && a.graceMs === b.graceMs;
}
